import React, { useState, useEffect, useRef } from 'react';
import MapPanel from "../basic/MapPanel";
import MapLegend from '../specific/MapLegend';
import Slider from '@mui/material/Slider';
import Button from '@mui/material/Button';
import {
    buildColoredMapPathsPerWork,
    buildOperacaoStatusGateFromData,
    createMapValueLegends,
    getHistCoordsPoint,
    parseMapNumericValue,
} from '../../usecases/mapPathUtils';

const getCoordsItems = (workData) => workData?.[3]?.histCoords || [];

const buildVelocidadePaths = (data, legends) =>
    buildColoredMapPathsPerWork({
        data,
        getItems: getCoordsItems,
        getShouldIncludeItem: (workData) => buildOperacaoStatusGateFromData(workData),
        legends,
        getValue: (item) => item.speed,
        getCoords: getHistCoordsPoint,
    });

const Map_velocidade = ({ data, implementWidthMeters }) => {
    const [legends, setLegends] = useState([]);
    const [path, setPath] = useState([]);
    const [marker, setMarker] = useState([]);
    const [centerMap, setCenterMap] = useState(null);
    const [loadingMap, setLoadingMap] = useState(true);
    const [sliderValue, setSliderValue] = useState([0, 100]);
    const [realMin, setRealMin] = useState(0);
    const [realMax, setRealMax] = useState(100);
    const isInitialLoadRef = useRef(true);

    const createLegends = (newMin, newMax) => {
        const newLegends = createMapValueLegends(newMin, newMax);
        setLegends(newLegends);
        return newLegends;
    };
    
    const handleSliderChange = (event, newValue) => {
        setSliderValue(newValue);
    };

    const applyLimits = () => {
        const [newMin, newMax] = sliderValue;
        createLegends(newMin, newMax);
    };  

    const processPath = () => {
        const currentHistory = data[3]?.histCoords || [];
        if (!currentHistory || currentHistory.length === 0) {
            return;
        }

        if (!legends || legends.length === 0) {
            return;
        }
        
        setPath(buildVelocidadePaths(data, legends));
    };

    const handleLegendChange = (legendId) => {
        const legendsCopy = legends.map((legend) =>
            legend.id === legendId ? { ...legend, show: !legend.show } : legend
        );
        setLegends(legendsCopy);
    };

    // Carregamento inicial: cria traçado segmentado colorido imediatamente
    useEffect(() => {
        isInitialLoadRef.current = true;
        const currentHistory = data[3]?.histCoords || [];
        
        if (currentHistory && currentHistory.length > 0) {
            // Calcula min/max da velocidade
            const min = currentHistory.reduce((acc, item) => {
                const vel = parseMapNumericValue(item.speed);
                return vel != null && vel > 0 && vel < acc ? vel : acc;
            }, Number.MAX_VALUE);
            const max = currentHistory.reduce((acc, item) => {
                const vel = parseMapNumericValue(item.speed) || 0;
                return vel > acc ? vel : acc;
            }, 0);

            const newRealMin = min !== Number.MAX_VALUE ? min : 0;
            const newRealMax = max;

            setRealMin(newRealMin);
            setRealMax(newRealMax);
            setSliderValue([newRealMin, newRealMax]);
            
            // Cria as legendas e obtém o retorno
            const newLegends = createLegends(newRealMin, newRealMax);

            // Marca que o carregamento inicial foi concluído
            isInitialLoadRef.current = false;

            // Processa o path segmentado colorido imediatamente (um path por trabalho)
            setPath(buildVelocidadePaths(data, newLegends));
            setLoadingMap(false);
    
            setMarker([
                { latitude: currentHistory[0].lat, longitude: currentHistory[0].lng, coordinateType: 'first' },
                { latitude: currentHistory[currentHistory.length - 1].lat, longitude: currentHistory[currentHistory.length - 1].lng, coordinateType: 'last' }
            ]);
            setCenterMap({ lat: currentHistory[0].lat, lng: currentHistory[0].lng });
        } else {
            setPath([]);
            setLoadingMap(false);
        }
    }, [data]);

    // Reprocessa caminho sempre que as legendas mudarem (após carregamento inicial)
    useEffect(() => {
        if (isInitialLoadRef.current) {
            return;
        }
        if (legends && legends.length > 0) {
            processPath();
        }
    }, [legends]);

    return (
        <div style={{width: '100%', alignItems:'center'}}>
            <h3>Variação de velocidade durante o trabalho</h3>
            {loadingMap ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '600px' }}>
                    <p>Carregando mapa...</p>
                </div>
            ) : (
                <>
                    <MapPanel markers={marker} zoomMap={17} centerMap={centerMap} linhas={path} height={600} implementWidthMeters={implementWidthMeters} />
                    <MapLegend legends={legends} onLegendChange={handleLegendChange} />
                    <div className="no-print" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', margin: '20px 0', gap: 8, width: '100%' }}>
                        <Slider
                            style={{ width: 'min(60%, 100%)', flex: 1, minWidth: 160, marginRight: '20px', marginLeft: '24px' }}
                            value={sliderValue}
                            onChange={handleSliderChange}
                            valueLabelDisplay="auto"
                            min={realMin}
                            max={realMax}
                            step={0.1}
                        />
                        <Button 
                            onClick={applyLimits} 
                            style={{ backgroundColor: '#ffca20', color: '#000', borderRadius: '5px' }}
                        >
                            Aplicar Limites
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

export default Map_velocidade;
